import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import decode from 'jwt-decode';
import { ApiFeatureLicense, ApiPermission } from '@allbin/mobilix-api-client';
import { IS_MOCK_AUTH_MODE } from '../auth/mock';

// Separate key in msw/gallery so the far-future stub token never leaks into a
// real dev session on the same origin.
export const PERSISTED_AUTH_STORE_KEY = IS_MOCK_AUTH_MODE
  ? 'mobilix-auth-mock'
  : 'mobilix-auth';

export interface AllAuthClaims {
  sub: string;
  email: string;
  tenant_id: string;
  // Present with value null for tenant-level users; a contractor id otherwise.
  organization_id: string | null;
  permissions: ApiPermission[];
  user_profile_id: string;
  exp: number;
  iat: number;
}

interface UserStore {
  uid: string;
  email: string;
  name: string;
  /** AllAuth tenant short code from token claims, e.g. 'lts'. */
  tenantCode: string;
  /** Internal 'region-*' id, mapped from tenantCode via GET /tenants. */
  tenant: string;
  /** Contractor id claim; null for tenant-level users. */
  organizationId: string | null;
  userProfileId?: string;
  role: string;
  organization: string | null;
  accessToken: string | null;
  permissions: ApiPermission[];
  licenses: ApiFeatureLicense[];
  /** Suppressed after an explicit "switch profile" (so the selector doesn't
   * instantly re-pick the same single profile) and after a mint failure
   * cleared the profile (so the selector doesn't retry it in a loop). */
  autoSelectProfile: boolean;
  setAccessToken: (token: string | null) => void;
  setUserProfileId: (id: string) => void;
  setRole: (role: string) => void;
  setOrganization: (organization: string | null) => void;
  setTenant: (tenant: string) => void;
  setName: (name: string) => void;
  setPermissions: (permissions: ApiPermission[]) => void;
  setLicenses: (licenses: ApiFeatureLicense[]) => void;
  clearProfile: (opts?: { suppressAutoSelect?: boolean }) => void;
}

// autoSelectProfile is deliberately NOT persisted: it is session-local UI
// state (suppress the selector auto-picking the same profile right after an
// explicit switch or a mint failure). Persisting it would leave a returning
// single-profile user stranded on the selector on every cold boot.
type PersistedUserStore = {
  userProfileId?: string | null;
  role?: string;
  organization?: string | null;
  accessToken?: string | null;
  permissions?: ApiPermission[];
};

const claimsFromToken = (token: string | null): Partial<UserStore> => {
  if (!token) {
    return {};
  }
  try {
    const claims = decode<AllAuthClaims>(token);
    return {
      uid: claims.sub,
      email: claims.email,
      tenantCode: claims.tenant_id,
      organizationId: claims.organization_id ?? null,
      permissions: claims.permissions || [],
    };
  } catch {
    return {};
  }
};

const useUserStore = create<UserStore>()(
  devtools(
    persist(
      (set, get) => ({
        uid: '',
        email: '',
        name: '',
        tenantCode: '',
        tenant: '',
        organizationId: null,
        userProfileId: undefined,
        role: '',
        organization: null,
        accessToken: null,
        permissions: [],
        licenses: [],
        autoSelectProfile: true,
        setAccessToken: (token) => {
          if (!token) {
            set({ accessToken: null });
            return;
          }

          const claims = claimsFromToken(token);
          // Keep the permissions array reference stable across renewals so
          // permission-selecting components don't re-render every 10 min.
          const current = get().permissions;
          if (
            claims.permissions &&
            claims.permissions.length === current.length &&
            claims.permissions.every((p, i) => p === current[i])
          ) {
            claims.permissions = current;
          }
          set({ accessToken: token, ...claims });
        },
        setUserProfileId: (id) =>
          set({ userProfileId: id, autoSelectProfile: true }),
        setRole: (role) => set({ role }),
        setOrganization: (organization) => set({ organization }),
        setTenant: (tenant) => set({ tenant }),
        setName: (name) => set({ name }),
        setPermissions: (permissions) => {
          const state = get();
          if (
            permissions.length !== state.permissions.length ||
            permissions.some((p, i) => p !== state.permissions[i])
          ) {
            set({ permissions });
          }
        },
        setLicenses: (licenses) => set({ licenses }),
        clearProfile: ({ suppressAutoSelect } = {}) =>
          set({
            uid: '',
            email: '',
            name: '',
            tenantCode: '',
            tenant: '',
            organizationId: null,
            userProfileId: undefined,
            role: '',
            organization: null,
            accessToken: null,
            permissions: [],
            licenses: [],
            autoSelectProfile: !suppressAutoSelect,
          }),
      }),
      {
        name: PERSISTED_AUTH_STORE_KEY,
        partialize: (state): PersistedUserStore => ({
          userProfileId: state.userProfileId ?? null,
          role: state.role,
          organization: state.organization,
          accessToken: state.accessToken,
          permissions: state.permissions,
        }),
        // Re-derive identity claims on boot so the app renders immediately
        // from persisted state; the token itself renews lazily (and may
        // already be expired — claims are still decodable). When the token is
        // gone but permissions persist (the boot-mint path), claims stay empty
        // so RequireLogin re-mints before mounting anything that reads uid.
        merge: (persisted, current) => {
          const stored = (persisted ?? {}) as PersistedUserStore;
          const accessToken = stored.accessToken ?? null;
          return {
            ...current,
            userProfileId: stored.userProfileId ?? undefined,
            role: stored.role ?? current.role,
            organization:
              stored.organization === undefined
                ? current.organization
                : stored.organization,
            accessToken,
            permissions: stored.permissions ?? current.permissions,
            ...claimsFromToken(accessToken),
          };
        },
      },
    ),
    { name: 'user-store', enabled: process.env.NODE_ENV !== 'test' },
  ),
);

export default useUserStore;
